import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { PasswordToggleDirective } from '../../directives/password-toggle.directive';

@Component({
  selector: 'app-modal-temp',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PasswordToggleDirective],
  templateUrl: './modal-temp.component.html',
  styleUrl: './modal-temp.component.css',
})
export class ModalTempComponent implements OnInit {
  showModal = false;
  passwordForm: FormGroup;
  passwordErrors: string[] = [];
  error: boolean = false;

  // Requisitos de contraseña
  passwordRequirements = [
    { key: 'minLength', met: false, message: 'Mínimo 8 caracteres' },
    { key: 'upperCase', met: false, message: 'Al menos una letra mayúscula' },
    { key: 'lowerCase', met: false, message: 'Al menos una letra minúscula' },
    { key: 'number', met: false, message: 'Al menos un número' },
    {
      key: 'specialChar',
      met: false,
      message: 'Al menos un carácter especial',
    },
    {
      key: 'matchPassword',
      met: false,
      message: 'Las contraseñas deben coincidir',
    },
  ];

  isSameActual: boolean = false;
  isSameNew: boolean = false;
  isSameConfirm: boolean = false;

  constructor(private authService: AuthService) {
    this.passwordForm = new FormGroup(
      {
        Email: new FormControl('', [Validators.required, Validators.email]),
        NewPassword: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
          this.createPasswordValidator(),
        ]),
        ConfirmPassword: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
        ]),
      },
      { validators: [this.passwordMatchValidator] }
    );

    // Suscribirse a cambios en ambos campos
    this.passwordForm.get('NewPassword')?.valueChanges.subscribe((value) => {
      this.validatePassword(value);
    });

    this.passwordForm.get('ConfirmPassword')?.valueChanges.subscribe(() => {
      const newPassword = this.passwordForm.get('NewPassword')?.value;
      this.validatePassword(newPassword);
    });
  }

  ngOnInit() {
    this.showModal = this.authService.getTempPassword();
    if (this.showModal) {
      const email = localStorage.getItem('email');
      if (email) {
        this.passwordForm.patchValue({ Email: email });
      }
    }
  }

  private createPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasSpecialChar = /[^a-zA-Z0-9]/.test(value);
      const hasMinLength = value.length >= 8;

      const passwordValid =
        hasUpperCase && hasLowerCase && hasSpecialChar && hasMinLength;

      return !passwordValid ? { invalidPassword: true } : null;
    };
  }

  private validatePassword(password: string) {
    if (!password) {
      this.passwordRequirements.forEach((req) => {
        req.met = false;
      });
      return;
    }

    this.passwordRequirements.forEach((req) => {
      if (req.key === 'minLength') {
        req.met = password.length >= 8;
      } else if (req.key === 'upperCase') {
        req.met = /[A-Z]/.test(password);
      } else if (req.key === 'lowerCase') {
        req.met = /[a-z]/.test(password);
      } else if (req.key === 'number') {
        req.met = /[0-9]/.test(password);
      } else if (req.key === 'specialChar') {
        req.met = /[^a-zA-Z0-9]/.test(password);
      }
    });

    const confirmPassword = this.passwordForm.get('ConfirmPassword')?.value;
    this.passwordRequirements.forEach((req) => {
      if (req.key === 'matchPassword') {
        req.met = password && confirmPassword && password === confirmPassword;
      }
    });
  }

  get allRequirementsMet(): boolean {
    return this.passwordRequirements.every((req) => req.met);
  }

  onSubmit() {
    if (this.passwordForm.valid) {
      if (
        this.passwordForm.value.NewPassword !==
        this.passwordForm.value.ConfirmPassword
      ) {
        this.isSameConfirm = true;
        return;
      }

      const request = {
        Email: this.passwordForm.get('Email')?.value,
        OldPassword: this.passwordForm.get('ConfirmPassword')?.value,
        NewPassword: this.passwordForm.get('NewPassword')?.value,
      };

      this.authService.changePassword(request).subscribe({
        next: (response) => {
          if (response.data.successful) {
            this.authService.setTempPassword(false);
            this.showModal = false;
            this.isSameConfirm = false;
            this.error = false;
          }
        },
        error: (error) => {
          if (error.status === 409) {
            this.isSameNew = true;
            this.error = false;
          } else {
            this.error = true;
          }
          console.error('Error al cambiar la contraseña:', error);
        },
      });
    }
  }

  private passwordMatchValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const newPassword = control.get('NewPassword')?.value;
    const confirmPassword = control.get('ConfirmPassword')?.value;

    if (newPassword === confirmPassword) {
      return null;
    }

    return { passwordMismatch: true };
  }
}
